import * as Sentry from '@sentry/nextjs';
import { atom, PrimitiveAtom } from 'jotai';

import { MLModelType } from 'src/stores/ml/types';
import { fetchWithConnectTimeout } from 'src/utils/fetch';
import { computeMd5InWorker } from 'src/utils/md5HashWorker';

import { downloadableContentsAtom } from './atoms';
import { evictCacheKey, evictStaleModels, getCachedModel, setCachedModel } from './modelCache';
import { ModelEntry } from './types';

export const DLC_MODEL_TYPES = [
  MLModelType.Antman,
  MLModelType.HumanVision,
  MLModelType.LID,
] as const;

export const STATIC_MODEL_URLS: Record<MLModelType, string> = {
  [MLModelType.Antman]: `/antman/17_2.0.0-antman.float16.tflite.enc`,
  [MLModelType.HumanVision]: `/humanvision/15_hvm550-fp16.tflite.enc`,
  // Cloudflare cache rule이 `*.tflite.enc` suffix를 대상으로 하므로 파일명 형식을 유지한다.
  [MLModelType.LID]: `/lid/model.float16-30df3c72a79e958ed35fd4b27499cf3e.tflite.enc`,
};

export const STATIC_MODEL_IDS: Record<MLModelType, string> = {
  [MLModelType.Antman]: '55b9923afc446299bb4c86a3608b4325',
  [MLModelType.HumanVision]: '1db720ffab806bee83c35af0ec250b2b',
  [MLModelType.LID]: '30df3c72a79e958ed35fd4b27499cf3e',
};

export const modelRegistryInitializedAtom = atom(false);

const INITIAL_MODEL_ENTRY: ModelEntry = { status: 'idle' };

const DLC_CONNECT_TIMEOUT_MS = 5_000;

function assertNonEmptyModelBuffer(buffer: ArrayBuffer, modelType: MLModelType) {
  if (buffer.byteLength === 0) {
    throw new Error(`empty model response: ${modelType}`);
  }
}

export const modelEntryAtoms: Record<MLModelType, PrimitiveAtom<ModelEntry>> = {
  [MLModelType.Antman]: atom<ModelEntry>(INITIAL_MODEL_ENTRY),
  [MLModelType.HumanVision]: atom<ModelEntry>(INITIAL_MODEL_ENTRY),
  [MLModelType.LID]: atom<ModelEntry>(INITIAL_MODEL_ENTRY),
};

export const loadStaticFallbackAtom = atom(null, async (_get, set, modelType: MLModelType) => {
  try {
    const staticUrl = STATIC_MODEL_URLS[modelType];
    const buffer = await fetchWithConnectTimeout(staticUrl, DLC_CONNECT_TIMEOUT_MS);
    set(modelEntryAtoms[modelType], {
      status: 'ready',
      modelId: STATIC_MODEL_IDS[modelType],
      buffer,
      source: 'fallback',
      cached: false,
    });
  } catch (error) {
    set(modelEntryAtoms[modelType], { status: 'error', error: String(error) });
    Sentry.captureException(error, { tags: { source: 'downloadableContents' } });
  }
});

interface FetchModelParams {
  modelType: MLModelType;
  forceFresh?: boolean;
}

export const fetchModelAtom = atom(
  null,
  async (get, set, { modelType, forceFresh = false }: FetchModelParams) => {
    const current = get(modelEntryAtoms[modelType]);
    if (current.status === 'loading') return;
    if (current.status === 'ready' && !forceFresh) return;

    set(modelEntryAtoms[modelType], { status: 'loading' });

    try {
      // 1) DLC 경로 시도
      const entries = get(downloadableContentsAtom);
      const entry = entries.find((e) => e.id === modelType);

      if (!entry) {
        Sentry.captureMessage(`[ModelRegistry] DLC entry not found: ${modelType}`);
        await set(loadStaticFallbackAtom, modelType);
        return;
      }

      if (!entry.md5Hash) {
        Sentry.captureMessage(`[ModelRegistry] md5Hash missing for ${modelType}, skipping cache`);
        const buffer = await fetchWithConnectTimeout(entry.url, DLC_CONNECT_TIMEOUT_MS);
        assertNonEmptyModelBuffer(buffer, modelType);
        const { buffer: modelBuffer, hash: computedModelId } = await computeMd5InWorker(buffer);
        set(modelEntryAtoms[modelType], {
          status: 'ready',
          modelId: computedModelId,
          buffer: modelBuffer,
          source: 'dlc',
          cached: false,
        });
        return;
      }

      const cacheKey = `${entry.id}#${entry.md5Hash}`;

      if (forceFresh) {
        await evictCacheKey(cacheKey);
      }

      let buffer = forceFresh ? undefined : await getCachedModel(cacheKey);
      const wasCache = !!buffer;

      if (!buffer) {
        buffer = await fetchWithConnectTimeout(entry.url, DLC_CONNECT_TIMEOUT_MS);
        assertNonEmptyModelBuffer(buffer, modelType);
        const result = await computeMd5InWorker(buffer);
        buffer = result.buffer;
        if (result.hash !== entry.md5Hash) {
          throw new Error(`md5 mismatch: expected=${entry.md5Hash}, actual=${result.hash}`);
        }
        // IDB 저장/정리는 background에서 best-effort로 처리
        void setCachedModel(cacheKey, buffer);
        void evictStaleModels(entry.id, cacheKey);
      }

      set(modelEntryAtoms[modelType], {
        status: 'ready',
        modelId: entry.md5Hash,
        buffer,
        source: 'dlc',
        versionKey: cacheKey,
        cached: wasCache,
      });
      return;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          source: 'downloadableContents',
        },
      });
    }

    await set(loadStaticFallbackAtom, modelType);
  }
);

export const invalidateAndRefetchModelAtom = atom(
  null,
  async (get, set, modelType: MLModelType) => {
    const entry = get(modelEntryAtoms[modelType]);
    if (!entry.cached) return;

    set(modelEntryAtoms[modelType], { status: 'idle' });
    await set(fetchModelAtom, { modelType, forceFresh: true });
  }
);
